import { useCallback, useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { Box, Typography, Button, Tooltip } from "@mui/material";
import MemoryIcon from "@mui/icons-material/Memory";
import LayersIcon from "@mui/icons-material/Layers";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ScienceIcon from "@mui/icons-material/Science";
import CategoryIcon from "@mui/icons-material/Category";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import DnsIcon from "@mui/icons-material/Dns";
import StorageIcon from "@mui/icons-material/Storage";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import TuneIcon from "@mui/icons-material/Tune";
import LogoutIcon from "@mui/icons-material/Logout";

import api from "./api";
import { MONO, tokens } from "./theme";
import { StatusDot } from "./components/ui";

const RAIL_BG = tokens.inkRail;
// Grouped so the rail's structure mirrors what an operator does: watch the rack,
// run models, hand out access.
const NAV = [
  { section: "Monitor", items: [
    { to: "/gpus", label: "GPUs", icon: MemoryIcon },
    { to: "/queue", label: "Queue", icon: LayersIcon },
  ]},
  { section: "Generate", items: [
    { to: "/generators", label: "Generators", icon: AutoAwesomeIcon },
    { to: "/playground", label: "Playground", icon: ScienceIcon },
  ]},
  { section: "Classify", items: [
    { to: "/classifiers", label: "Classifiers", icon: CategoryIcon },
    { to: "/classifier-playground", label: "Playground", icon: FactCheckIcon },
  ]},
  { section: "Serve", items: [
    { to: "/vllm-models", label: "vLLM models", icon: DnsIcon },
    { to: "/model-registry", label: "Model Registry", icon: StorageIcon },
  ]},
  { section: "Access", items: [
    { to: "/tokens", label: "API tokens", icon: VpnKeyIcon },
  ]},
  { section: "System", items: [
    { to: "/settings", label: "Settings", icon: TuneIcon },
  ]},
];

function RailFooter() {
  const [ov, setOv] = useState(null);
  const load = useCallback(() => api.get("/api/overview", { silent: true }).then(setOv).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  if (!ov) return null;
  const online = ov.gpus.count > 0;
  const usedPct = ov.gpus.total_mb ? Math.round((ov.gpus.used_mb / ov.gpus.total_mb) * 100) : 0;

  const Row = ({ dot, dotColor, label, value }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.3 }}>
      {dot ? <StatusDot color={dotColor} pulse={online} size={7} /> : <Box sx={{ width: 7 }} />}
      <Typography sx={{ ...railLabel }}>{label}</Typography>
      <Typography sx={{ fontFamily: MONO, fontSize: ".72rem", color: "#dfe8f5", ml: "auto" }}>{value}</Typography>
    </Box>
  );

  return (
    <Box sx={{ px: 2, py: 1.5, mx: 1.25, mb: 1, borderRadius: 2, background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.06)" }}>
      <Typography sx={{ ...railEyebrow, mb: 0.75 }}>Platform</Typography>
      <Row dot dotColor={online ? tokens.ok : tokens.warn} label={ov.gpus.mock ? "GPUs (mock)" : "GPUs online"} value={ov.gpus.count} />
      <Row label="VRAM in use" value={`${usedPct}%`} />
      <Row label="Running" value={ov.queue.running} />
      <Row label="Queued" value={ov.queue.pending} />
    </Box>
  );
}

const railEyebrow = {
  fontFamily: MONO,
  fontSize: ".6rem",
  letterSpacing: ".18em",
  textTransform: "uppercase",
  color: "#66789a",
  fontWeight: 500,
};
const railLabel = { fontSize: ".82rem", color: "#9db0cc" };

export default function Layout({ onSignOut }) {
  const navigate = useNavigate();

  const signOut = async () => {
    try {
      await api.post("/api/logout", {}, { silent: true });
    } catch {
      /* clear local state regardless */
    }
    onSignOut();
    navigate("/login", { replace: true });
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        component="nav"
        sx={{
          width: 238,
          flex: "0 0 238px",
          bgcolor: RAIL_BG,
          display: "flex",
          flexDirection: "column",
          py: 2.5,
          position: "sticky",
          top: 0,
          height: "100vh",
        }}
      >
        {/* Wordmark */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2.25, mb: 3 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "9px",
              background: tokens.vramGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 10px rgba(46,139,230,.4)",
            }}
          >
            <MemoryIcon sx={{ color: "#fff", fontSize: 19 }} />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".98rem", lineHeight: 1.1 }}>
              Generator
            </Typography>
            <Typography sx={{ ...railEyebrow, color: "#7f92b4" }}>Console</Typography>
          </Box>
        </Box>

        {/* Grouped nav */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 1.25 }}>
          {NAV.map((group) => (
            <Box key={group.section} sx={{ mb: 2 }}>
              <Typography sx={{ ...railEyebrow, px: 1.25, mb: 0.75 }}>{group.section}</Typography>
              {group.items.map(({ to, label, icon: Icon }) => (
                <Box
                  key={to}
                  component={NavLink}
                  to={to}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    px: 1.25,
                    py: 0.95,
                    mb: 0.25,
                    borderRadius: 2,
                    textDecoration: "none",
                    color: "#9db0cc",
                    fontSize: ".9rem",
                    fontWeight: 500,
                    position: "relative",
                    transition: "background .15s ease, color .15s ease",
                    "& svg": { fontSize: 19, color: "#66789a", transition: "color .15s ease" },
                    "&:hover": { background: "rgba(255,255,255,.05)", color: "#fff", "& svg": { color: "#c3d2ea" } },
                    "&.active": {
                      background: "rgba(46,139,230,.16)",
                      color: "#fff",
                      "& svg": { color: tokens.signal },
                    },
                    "&.active::before": {
                      content: '""',
                      position: "absolute",
                      left: -6,
                      top: 8,
                      bottom: 8,
                      width: 3,
                      borderRadius: 3,
                      background: tokens.signal,
                    },
                  }}
                >
                  <Icon />
                  {label}
                </Box>
              ))}
            </Box>
          ))}
        </Box>

        <RailFooter />

        <Box sx={{ px: 2.25 }}>
          <Tooltip title="End this session">
            <Button
              onClick={signOut}
              startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
              sx={{ color: "#7f92b4", fontSize: ".82rem", px: 0, "&:hover": { color: "#fff", bgcolor: "transparent" } }}
            >
              Sign out
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <Box component="main" sx={{ flex: 1, p: { xs: 2, md: 4 }, overflow: "auto", maxWidth: 1320, mx: "auto", width: "100%" }}>
        <Outlet />
      </Box>
    </Box>
  );
}
